/* LIST VIEW + COMPARE for the species landing pages.
   Paired with assets/stone-list.css, snippets/card-stone-row.liquid and the
   `<template id="sb-list-head">` in sections/stone-browse.liquid.

   ── PROGRESSIVE ENHANCEMENT, SAME CONTRACT AS stone-browse.js ────────────────
   The server renders the GRID, and the grid is a complete, working page. This file
   adds a second layout and a compare feature. If it fails to parse, fails to load,
   or the browser refuses a custom element, the page is exactly what it was before
   list view existed. Do not invert that: nothing here may become load-bearing for
   browsing or buying a stone.

   ── WHY THE CELLS ARE NOT BUILT HERE ─────────────────────────────────────────
   Every spec cell is rendered by Liquid inside the theme's own `<li class="grid__
   item">`. That is not incidental — this template paginates with INFINITE SCROLL,
   and assets/pagination.js fetches the next page and appends the `.grid__item`
   elements it finds. Rows therefore arrive with their cells and their compare box
   already attached, and this file never has to know that paging happened. The only
   thing it does about appended rows is re-tick the boxes (see `sync`), because the
   selection lives in localStorage and the fresh markup has never seen it.

   ── WHY THE SELECTION IS A PAYLOAD, NOT A LIST OF IDS ────────────────────────
   A shopper ticks a sapphire, changes the filter to Pear, ticks another, then opens
   compare. The second render knows nothing about the first stone — it is not on the
   page and no Liquid on the page can reach it. So each tick stores everything the
   compare view needs (`data-cmp` on the checkbox, plus the row's own shape SVG),
   and the overlay is built entirely from storage. That is also what lets compare
   span species: sapphire against tanzanite is the case where the durability row
   stops being a constant.

   ── THE ONE IDEA THAT IS NOT A TABLE ─────────────────────────────────────────
   Every stone photo on this store is normalised so the stone fills ~76% of its
   plate. That is right for a grid and a lie in a comparison — a 4 mm stone and a
   14 mm stone are drawn the same size. We hold length_mm and width_mm on ~99% of
   the catalogue, so the compare overlay draws the silhouettes at TRUE RELATIVE
   SCALE from one shared px-per-mm, with a labelled reference bar. It answers the
   question a photo grid structurally cannot. */
(function () {
  'use strict';

  var VIEW_KEY = 'sr.stoneview';
  // 🚨 BUMPED v1 -> v2 ON 2026-08-06 BECAUSE `pn` CHANGED UNITS, NOT SHAPE.
  // The diamond page emitted `pn` in DOLLARS while the 21 Liquid landing pages emit
  // CENTS, so a mixed tray scored an $11,625 diamond below a $545 sapphire and
  // labelled it "lowest". Fixing the producer is not enough: this tray is
  // localStorage, not session, so anything a shopper had already added would keep
  // its dollar-denominated `pn` and go on comparing wrong forever. The bump drops
  // those entries. It costs everyone their current tray once — a transient shopping
  // aid, not saved work — and is the only way to retire the bad values.
  // ⚠️ ANY future change to what a payload FIELD MEANS needs this bump too. A change
  // to the field SET does not: a missing key reads as absent and degrades cleanly,
  // which is why v1 survived Carat/Clarity/Cut being added.
  var CMP_KEY = 'sr.compare.v2';
  var MAX = 4;                  // four columns is the most that stays readable
  var SIZE_BOX = 130;           // px given to the LARGEST dimension in the set

  var doc = document;
  var root = doc.documentElement;

  // localStorage throws in Safari private mode. Every call site treats a failure
  // as "no stored state", which degrades to grid + an empty compare tray.
  function read(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) { return fallback; }
  }
  function write(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* no-op */ }
  }
  // ⚠️ THE VIEW MODE IS STORED RAW, NOT AS JSON, AND THAT IS NOT A STYLE CHOICE.
  // The reader is the inline script in sections/stone-browse.liquid — it has to run
  // before the grid is parsed, so it cannot wait for this file, and it compares
  // `getItem(...) === 'list'`. Writing it through JSON.stringify stores `"list"`
  // WITH the quotes, which never equals `list`: the switch worked perfectly, the
  // choice was saved, and it silently reverted to grid on every page load. Nothing
  // errors and nothing looks broken until you reload.
  function writeView(mode) {
    try { localStorage.setItem(VIEW_KEY, mode); } catch (e) { /* no-op */ }
  }

  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  var X_SVG = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8"/></svg>';
  var ROUND = '<circle cx="20" cy="20" r="17"/>';

  // The silhouette is written by us, but it goes out to localStorage and comes back
  // through innerHTML — and localStorage is editable by whoever owns the browser.
  // So it is re-checked on the way OUT rather than trusted because of where it came
  // from: the drawing primitives gem-shape-icon actually emits, and nothing that
  // could carry script. Anything else falls back to a round outline, which is the
  // same fallback the snippet itself uses for an undrawn shape.
  // ⚠️ CLOSING TAGS ARE PART OF THE STRING. `gem-shape-icon.liquid` writes
  // `<path d="..."/>`, but this is read back with `.innerHTML` off a live SVG, and
  // the HTML serializer expands every one to `<path d="..."></path>`. A whitelist
  // that only allowed opening tags rejected all of it and fell back to the round
  // outline — with `preserveAspectRatio="none"` that circle stretches to fill the
  // box, so the true-scale row still drew something plausible and correctly sized
  // and EVERY shape was an ellipse. `</?` is the whole fix.
  function safeSvg(s) {
    if (!s || /<\s*script|\son[a-z]+\s*=|javascript:/i.test(s)) return ROUND;
    if (!/^(\s*<\/?(path|circle|ellipse)\b[^<>]*>\s*)+$/i.test(s)) return ROUND;
    return s;
  }

  /* ==========================================================================
     VIEW MODE
     ========================================================================== */
  // ⚠️ `#product-grid` IS THE THEME'S GRID, AND IT IS NOT THE ONLY ONE ANY MORE.
  // /collections/diamonds cannot use the theme's grid at all — `collection.filters`
  // returns empty over 5,000 products (diamonds are 7,890), so diamond-browse.js
  // renders its own grid from shards and HIDES the server-rendered one. On that page
  // `#product-grid` still exists, still holds 24 `li.grid__item`s, and is display:
  // none — so a lookup by id there finds a real element that nothing can ever see:
  // the list header would be inserted next to it and the MutationObserver would
  // watch a grid that never changes. `[data-sbl-grid]` lets a page nominate its own,
  // and no page that predates this carries the attribute, so the 21 species landing
  // pages resolve to exactly what they always did.
  var grid = doc.querySelector('[data-sbl-grid]') || doc.getElementById('product-grid');

  function applyView(mode, persist) {
    if (mode === 'list') root.setAttribute('data-sb-view', 'list');
    else root.removeAttribute('data-sb-view');
    Array.prototype.forEach.call(doc.querySelectorAll('.sb-view'), function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.sbView === mode));
    });
    if (persist) writeView(mode);
  }

  function initView() {
    var buttons = doc.querySelectorAll('.sb-view');
    if (!buttons.length) return;

    // The inline script in the section has already set the attribute (that is what
    // stops the grid painting and then re-laying out); this only catches the
    // buttons up to it.
    applyView(root.getAttribute('data-sb-view') === 'list' ? 'list' : 'grid', false);

    Array.prototype.forEach.call(buttons, function (b) {
      b.addEventListener('click', function () {
        applyView(b.dataset.sbView, true);
      });
    });

    ensureHead();
  }

  // The column header is authored in Liquid and parked in a <template>; it has to
  // live immediately before the grid, which belongs to a different section.
  // Skipped when there are no rows — a header over an empty result set reads as
  // a broken table, and the panel's own empty state already explains the zero.
  function ensureHead() {
    var tpl = doc.getElementById('sb-list-head');
    if (tpl && grid && grid.querySelector('li.grid__item') &&
        !doc.querySelector('[data-sbl-head]')) {
      grid.parentNode.insertBefore(tpl.content.cloneNode(true), grid);
    }
  }

  /* ==========================================================================
     COMPARE — selection state
     ========================================================================== */
  var picked = read(CMP_KEY, []);
  if (!Array.isArray(picked)) picked = [];
  picked = picked.slice(0, MAX);

  function indexOf(id) {
    for (var i = 0; i < picked.length; i++) if (String(picked[i].id) === String(id)) return i;
    return -1;
  }

  function boxes() { return doc.querySelectorAll('.sbl-cmp__i'); }

  function payloadOf(input) {
    try { return JSON.parse(input.dataset.cmp || 'null'); } catch (e) { return null; }
  }

  // Re-tick every box on the page from storage, and close the gate at the cap.
  // Runs on load AND every time infinite scroll appends rows, which arrive as fresh
  // markup that has never seen the selection.
  function sync() {
    var full = picked.length >= MAX;
    Array.prototype.forEach.call(boxes(), function (input) {
      var p = payloadOf(input);
      if (!p) return;
      var on = indexOf(p.id) !== -1;
      input.checked = on;
      input.disabled = full && !on;
    });
    renderTray();
  }

  function toggle(input) {
    var p = payloadOf(input);
    if (!p) return;
    var at = indexOf(p.id);
    if (at !== -1) {
      picked.splice(at, 1);
    } else {
      if (picked.length >= MAX) { input.checked = false; return; }
      // The silhouette is read off THIS row rather than carried in the payload, so
      // `gem-shape-icon.liquid` stays the only place those paths exist. A stone can
      // only ever be ticked from a page that drew it, so it is always available here.
      var svg = input.closest('.sbl') && input.closest('.sbl').querySelector('.sbl__ico svg');
      p.svg = svg ? svg.innerHTML : '';
      picked.push(p);
    }
    write(CMP_KEY, picked);
    sync();
    if (dlg && dlg.open) paint();       // keep an open overlay honest
  }

  function remove(id) {
    var at = indexOf(id);
    if (at === -1) return;
    picked.splice(at, 1);
    write(CMP_KEY, picked);
    sync();
    if (dlg && dlg.open) { picked.length >= 2 ? paint() : dlg.close(); }
  }

  function clearAll() {
    picked = [];
    write(CMP_KEY, picked);
    sync();
    if (dlg && dlg.open) dlg.close();
  }

  /* ==========================================================================
     COMPARE — the tray
     ========================================================================== */
  var tray, trayItems, trayGo, trayHint;

  function buildTray() {
    tray = el('div', 'sct');
    tray.hidden = true;
    var inner = el('div', 'sct__in');
    trayItems = el('div', 'sct__items');
    var acts = el('div', 'sct__acts');
    trayHint = el('span', 'sct__hint');
    trayGo = el('button', 'sct__go');
    trayGo.type = 'button';
    trayGo.addEventListener('click', open);
    var clear = el('button', 'sct__clear', 'Clear');
    clear.type = 'button';
    clear.addEventListener('click', clearAll);
    acts.appendChild(trayHint);
    acts.appendChild(trayGo);
    acts.appendChild(clear);
    inner.appendChild(trayItems);
    inner.appendChild(acts);
    tray.appendChild(inner);
    doc.body.appendChild(tray);
  }

  function renderTray() {
    if (!tray) return;
    // A fixed bar over the last row of stones reads as a broken page, not as a
    // tray. The body makes room for it while it is up.
    doc.body.classList.toggle('sct-open', picked.length > 0);
    if (!picked.length) {
      tray.classList.remove('is-open');
      tray.hidden = true;
      return;
    }
    // `is-open` only ever plays the slide-in ONCE — adding a second stone must not
    // re-run it. The bar's resting position is its normal one either way; see the
    // keyframes note in stone-list.css for why this cannot leave it stuck.
    tray.hidden = false;
    if (!tray.classList.contains('is-open')) tray.classList.add('is-open');

    trayItems.textContent = '';
    picked.forEach(function (p) {
      var item = el('div', 'sct-item');
      if (p.img) {
        var img = doc.createElement('img');
        img.src = p.img;
        img.alt = '';
        img.loading = 'lazy';
        item.appendChild(img);
      }
      item.appendChild(el('span', 'sct-item__t', p.t));
      var x = el('button', 'sct-item__x');
      x.type = 'button';
      x.setAttribute('aria-label', 'Remove ' + p.t + ' from compare');
      x.innerHTML = X_SVG;
      x.addEventListener('click', function () { remove(p.id); });
      item.appendChild(x);
      trayItems.appendChild(item);
    });
    for (var i = picked.length; i < MAX; i++) trayItems.appendChild(el('div', 'sct-slot'));

    trayGo.textContent = 'Compare ' + picked.length;
    trayGo.disabled = picked.length < 2;
    trayHint.textContent = picked.length < 2 ? 'Pick one more' : '';
  }

  /* ==========================================================================
     COMPARE — the overlay
     ========================================================================== */
  var dlg, dlgTitle, dlgScroll, dlgOnly;

  function buildDialog() {
    dlg = doc.createElement('dialog');
    dlg.className = 'sc';
    var inner = el('div', 'sc__in');

    var top = el('header', 'sc__top');
    dlgTitle = el('h2', 'sc__h');
    var opts = el('div', 'sc__opts');

    var only = el('label', 'sc__only');
    dlgOnly = doc.createElement('input');
    dlgOnly.type = 'checkbox';
    dlgOnly.addEventListener('change', function () {
      dlg.classList.toggle('is-diffonly', dlgOnly.checked);
    });
    only.appendChild(dlgOnly);
    only.appendChild(el('span', null, 'Differences only'));

    var x = el('button', 'sc__x');
    x.type = 'button';
    x.setAttribute('aria-label', 'Close compare');
    x.innerHTML = X_SVG;
    x.addEventListener('click', function () { dlg.close(); });

    opts.appendChild(only);
    opts.appendChild(x);
    top.appendChild(dlgTitle);
    top.appendChild(opts);

    dlgScroll = el('div', 'sc__scroll');
    inner.appendChild(top);
    inner.appendChild(dlgScroll);
    dlg.appendChild(inner);

    // Click OUTSIDE the panel closes. A <dialog>'s own box is the backdrop area as
    // far as click targets go, so a press that lands on the element itself (never
    // on a child) is a backdrop press.
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
    doc.body.appendChild(dlg);
  }

  function open() {
    if (picked.length < 2) return;
    if (!dlg) buildDialog();
    paint();
    // showModal gives the focus trap, the inert background and Escape for free.
    // Older engines without it still get the panel, just without those.
    if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
  }

  function num(v) {
    var n = parseFloat(v);
    return isFinite(n) && n > 0 ? n : 0;
  }

  function sizeText(p) {
    var L = num(p.l), W = num(p.w);
    if (!L) return '—';
    return W ? L + ' × ' + W + ' mm' : L + ' mm';
  }

  // A row is "the same" when every stone reads identically. Compared on the
  // rendered string, because that is what the shopper is being asked to compare —
  // "Natural" and "natural" would be a difference on screen and are not one here.
  function allSame(values) {
    for (var i = 1; i < values.length; i++) {
      if (String(values[i]).toLowerCase() !== String(values[0]).toLowerCase()) return false;
    }
    return true;
  }

  function addRow(key, values, opts) {
    opts = opts || {};
    var row = el('div', 'sc__row' + (opts.cls ? ' ' + opts.cls : ''));
    var k = el('div', 'sc__k' + (opts.kcls ? ' ' + opts.kcls : ''), key);
    if (opts.kextra) k.appendChild(opts.kextra);
    row.appendChild(k);
    values.forEach(function (v, i) {
      var cell = el('div', 'sc__v');
      if (v && v.nodeType) cell.appendChild(v);
      else cell.textContent = v == null || v === '' ? '—' : v;
      if (opts.best === i) cell.appendChild(el('span', 'sc__best', opts.bestLabel));
      row.appendChild(cell);
    });
    if (!opts.neverSame && allSame(values.map(function (v) {
      return v && v.nodeType ? (v.dataset.cmp || '') : (v == null ? '' : v);
    }))) row.classList.add('is-same');
    dlgScroll.appendChild(row);
    return row;
  }

  // index of the single best value, or -1 when there is a tie or nothing to rank
  function bestIndex(nums, want) {
    var at = -1, bestv = null, ties = 0;
    nums.forEach(function (n, i) {
      if (!n) return;
      if (bestv === null || (want === 'max' ? n > bestv : n < bestv)) {
        bestv = n; at = i; ties = 1;
      } else if (n === bestv) { ties++; }
    });
    return ties === 1 && nums.filter(Boolean).length === nums.length ? at : -1;
  }

  function paint() {
    dlgScroll.textContent = '';
    // Only the COUNT. The column WIDTH lives in stone-list.css because it changes
    // at the phone breakpoint, and an inline style set here would beat the media
    // query that has to narrow it.
    dlg.style.setProperty('--sc-n', picked.length);
    dlgTitle.textContent = 'Compare ' + picked.length + ' stone'
      + (picked.length === 1 ? '' : 's');

    /* ---- head: 360 / photo, title, price ---- */
    var heads = picked.map(function (p) {
      var box = el('div', 'sc-head');
      var media = el('a', 'sc-head__m');
      media.href = p.u;
      if (p.img) {
        var img = doc.createElement('img');
        img.src = p.img;
        img.alt = p.t;
        media.appendChild(img);
      }
      // The turntables run side by side. `stone-hover-spin` upgrades on insert and
      // takes its hover box from `parentElement` when there is no card around it,
      // which is exactly `.sc-head__m`. Only the ~32-70% of stones Stuller filmed
      // have a clip; the rest keep the still, as they do on a card.
      if (p.spin) {
        var spin = doc.createElement('stone-hover-spin');
        spin.className = 'shs';
        spin.dataset.src = p.spin;
        spin.innerHTML =
          '<video class="shs__v" muted loop playsinline preload="none" ' +
          'disablepictureinpicture tabindex="-1" aria-hidden="true"></video>' +
          '<button type="button" class="shs__badge" aria-label="' +
          String(p.t).replace(/"/g, '&quot;') + ' — play 360 degree rotating view">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
          'aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>' +
          '<span>360°</span></button>';
        media.appendChild(spin);
      }
      box.appendChild(media);

      var a = el('a', 'sc-head__t', p.t);
      a.href = p.u;
      box.appendChild(a);
      box.appendChild(el('span', 'sc-head__p', p.p));
      if (p.av === false) box.appendChild(el('span', 'sbl__sold', 'Sold'));

      // Remove only while a comparison would survive it: the overlay never opens on
      // fewer than two, so removing one of two must not leave a one-stone compare.
      if (picked.length > 2) {
        var rm = el('button', 'sc-head__x');
        rm.type = 'button';
        rm.innerHTML = X_SVG + '<span>Remove</span>';
        rm.addEventListener('click', function () { remove(p.id); });
        box.appendChild(rm);
      }

      box.dataset.cmp = p.id;            // never "same" — see neverSame below
      return box;
    });
    addRow('', heads, { cls: 'sc__row--head', neverSame: true });

    /* ---- TRUE RELATIVE SCALE ---- */
    var maxDim = 0;
    picked.forEach(function (p) {
      var L = num(p.l), W = num(p.w);
      maxDim = Math.max(maxDim, L, W);
    });
    var ppm = maxDim > 0 ? SIZE_BOX / maxDim : 0;

    var arts = picked.map(function (p) {
      var wrap = el('div', 'sc-size');
      var L = num(p.l), W = num(p.w);
      if (!L || !ppm) {
        wrap.appendChild(el('span', 'sc-size__n', 'not measured'));
        wrap.dataset.cmp = 'none';
        return wrap;
      }
      var art = el('div', 'sc-size__art');
      var w = (W ? W : L) * ppm;
      var h = L * ppm;
      // BOTH dimensions known -> stretch the silhouette to the real footprint
      // (`preserveAspectRatio="none"`), which is the whole point: an 8x4 marquise
      // must draw twice as long as it is wide. Width missing (56% of
      // mozambique-garnet, the worst page) -> keep the drawing's own proportions
      // and scale it so its LONGEST axis is the true length. The length is then
      // still exact and the width is visibly a convention, marked with ~.
      var par = W ? 'none' : 'xMidYMid meet';
      art.innerHTML = '<svg viewBox="0 0 40 40" preserveAspectRatio="' + par +
        '" width="' + Math.round(w) + '" height="' + Math.round(h) +
        '" aria-hidden="true">' + safeSvg(p.svg) + '</svg>';
      wrap.appendChild(art);
      var n = el('span', 'sc-size__n', sizeText(p));
      if (!W) {
        n.appendChild(el('span', 'sc-size__approx', ' ~ width not recorded'));
      }
      wrap.appendChild(n);
      wrap.dataset.cmp = sizeText(p);
      return wrap;
    });

    // The reference bar. Its length is chosen so it fits the label column at
    // whatever px-per-mm this particular set produced — a fixed 5 mm bar is 54px
    // beside a 12 mm stone and 162px beside a 4 mm one.
    //
    // ⚠️ THIS IS A MAP SCALE, NOT A RULER, AND THE ROW IS DELIBERATELY UNLABELLED.
    // `ppm` is renormalised per comparison so the biggest stone in the set always
    // gets SIZE_BOX px, which means the same stone draws at a different size
    // depending on its company — and CSS px has no fixed physical length anyway
    // (zoom, DPR, OS scaling). So the drawings are never life-size: on a set
    // topping out at 8.53 mm, ppm is 130/8.53 = 15.24 and the 5 mm bar comes out
    // 76px, against the 18.9px a nominal 96dpi display needs — about 4x life. The row
    // used to be captioned "Actual size", meaning "the actual size RELATIONSHIP"
    // as against the photo grid, where every stone is normalised to ~76% plate
    // fill. Read cold above a bar marked "5 MM" it promised physical accuracy
    // instead, and was the only thing on the page making that claim — the footnote
    // three lines below says "true RELATIVE scale". A map does not caption its
    // scale bar, so neither do we: the bar is the legend and it stands alone.
    // OWNER RULE 2026-08-02. Do not reinstate a visible label here.
    var ref = el('div', 'sc-scale');
    // The row still needs a NAME even though it no longer shows one: the bar is a
    // picture and its "5 mm" is meaningless read aloud, so both are hidden from
    // assistive tech and this carries the label instead. `.visually-hidden` is
    // `position: absolute`, so it adds nothing to the flex column.
    ref.appendChild(el('span', 'visually-hidden', 'Size to scale'));
    if (ppm) {
      var mm = 1;
      [1, 2, 5, 10].forEach(function (c) { if (c * ppm <= 90) mm = c; });
      var bar = el('div', 'sc-scale__bar');
      bar.style.width = Math.round(mm * ppm) + 'px';
      bar.setAttribute('aria-hidden', 'true');
      ref.appendChild(bar);
      var barText = el('span', 'sc-scale__t', mm + ' mm');
      barText.setAttribute('aria-hidden', 'true');
      ref.appendChild(barText);
    }
    addRow('', arts, {
      cls: 'sc__row--size', kcls: 'sc__k--size', kextra: ref,
      best: bestIndex(picked.map(function (p) { return num(p.l) * (num(p.w) || num(p.l)); }), 'max'),
      bestLabel: 'largest'
    });

    /* ---- the specs ---- */
    // Colour leads. It is a column on only 7 of the 21 landing pages (it is one
    // repeated word on the rest), but it is a row here ALWAYS — compare is the one
    // surface that crosses species, and a red ruby beside a green emerald is the
    // clearest difference on the page. Same reasoning as Gem type and Durability,
    // neither of which is ever a column either.
    // ── THE 4Cs, WHERE THE STONE HAS THEM ───────────────────────────────────────
    // Guarded exactly like Origin below: a row appears only when at least one stone
    // in the set carries the field, so this is INERT on the 21 species pages —
    // card-stone-row.liquid's payload has no ct/cl/cg and never gains one, because
    // coloured stones are not graded on these scales. It exists for the diamond
    // browse page, where carat, clarity and cut ARE the columns, and where compare
    // without them would be a comparison of two diamonds that omits the two facts a
    // shopper is choosing between. Guarding rather than always-drawing also keeps
    // the mixed case honest: pick a sapphire and a diamond and Clarity shows the
    // diamond's grade against an em-dash, which is the truth.
    if (picked.some(function (p) { return p.ct; })) {
      addRow('Carat', picked.map(function (p) { return p.ct ? p.ct + ' ct' : ''; }), {
        best: bestIndex(picked.map(function (p) { return num(p.ct); }), 'max'),
        bestLabel: 'largest'
      });
    }
    addRow('Color', picked.map(function (p) { return p.c; }));
    if (picked.some(function (p) { return p.cl; })) {
      addRow('Clarity', picked.map(function (p) { return p.cl; }));
    }
    if (picked.some(function (p) { return p.cg; })) {
      addRow('Cut', picked.map(function (p) { return p.cg; }));
    }
    addRow('Shape', picked.map(function (p) { return p.sh; }));
    addRow('Formation', picked.map(function (p) { return p.f; }));
    addRow('Gem type', picked.map(function (p) { return p.g; }));
    // The row the fill-rate table said was our best column and the variance table
    // said was a constant. Both are true: constant DOWN a species page, a real
    // difference ACROSS species — which is exactly what compare is for.
    //
    // ⚠️ MATCHED ON THE TIER ALONE, NOT ON THE PRINTED LINE. Mohs is on ~98% of
    // stones, so three sapphires that are all "Excellent for daily wear" read as a
    // DIFFERENCE the moment one of them is missing its hardness — the row lights up
    // to announce that a suffix is absent. `dataset.cmp` carries what is actually
    // being compared and the cell prints the rest.
    addRow('Durability', picked.map(function (p) {
      var cell = el('span', null, p.wr ? p.wr + (p.mh ? ' · Mohs ' + p.mh : '') : '—');
      cell.dataset.cmp = p.wr || '';
      return cell;
    }));
    if (picked.some(function (p) { return p.or; })) {
      addRow('Origin', picked.map(function (p) { return p.or; }));
    }
    // No Item # row — OWNER RULE 2026-07-31, see card-stone-row.liquid. The serial
    // is an internal identifier and is not in the payload at all.
    addRow('Price', picked.map(function (p) { return p.p; }), {
      best: bestIndex(picked.map(function (p) { return num(p.pn); }), 'min'),
      bestLabel: 'lowest'
    });

    var note = el('p', 'sc__note',
      'Sizes are drawn to true relative scale from each stone’s measured length and '
      + 'width — the outlines are our shape silhouettes, not photographs, so the '
      + 'proportions are the stone’s and the facets are illustrative.');
    dlgScroll.appendChild(note);
  }

  /* ==========================================================================
     WIRING
     ========================================================================== */
  function boot() {
    initView();
    buildTray();
    sync();

    // Delegated, so rows appended by infinite scroll are live without rebinding.
    doc.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('sbl-cmp__i')) toggle(t);
    });

    // ...but their CHECKED state still has to be restored: pagination.js appends
    // freshly-fetched markup, which has never seen localStorage.
    watch();

    // Compare survives navigation, so a shopper returning from a product page must
    // find the tray as they left it — and a tick made in ANOTHER tab must not leave
    // this one showing a stale set.
    window.addEventListener('storage', function (e) {
      if (e.key !== CMP_KEY) return;
      picked = read(CMP_KEY, []) || [];
      sync();
      if (dlg && dlg.open) { picked.length >= 2 ? paint() : dlg.close(); }
    });

    /* 🚨 THE RE-TICK HOOK, FOR A GRID THIS FILE CANNOT WATCH (2026-08-17).
       The MutationObserver above is bound to `grid` — `[data-sbl-grid]` or the
       theme's `#product-grid` — which is how a paginated Liquid grid gets its
       freshly-appended rows re-ticked from storage. The ring/pendant/earring
       builder's stone grid is neither: it is built entirely in JS and pages with
       its own "View more" button, and nominating it via `data-sbl-grid` is NOT the
       fix — that attribute also selects the `html[data-sb-view='list']` rules in
       stone-list.css, which would relay out the builder's grid the moment a shopper
       had once chosen list view on a species page.

       So the builder calls this after each `draw()`. It is the SAME `sync`, not a
       copy: the alternative was ring-builder-stones.js reading `sr.compare.v2`
       itself, which is a second reader of a key whose meaning this file owns (and
       whose version it bumps — see CMP_KEY). Exported on `window` rather than
       returned because the consumer is a different file loaded in either order;
       absent, a builder card simply renders unticked, which is today's behaviour. */
    window.SRCompare = { sync: sync };
  }

  var watcher = null;
  function watch() {
    if (!grid || !('MutationObserver' in window)) return;
    if (!watcher) {
      watcher = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { sync(); return; }
        }
      });
    }
    watcher.disconnect();
    watcher.observe(grid, { childList: true });
  }

  /* 🚨 AFTER AN IN-PLACE RENDER THE GRID IS A DIFFERENT ELEMENT (2026-09-15). The instant
     filtering pilot (sr-instant-filters.js) replaces everything inside #ProductGridContainer,
     so `grid` above points at a detached list: the header was dropped with the old markup, the
     observer watches rows nobody can see, and the new rows have never been ticked from storage.
     This finds the grid again and redoes those three things. Called by that file after each
     render; a page that never renders in place never calls it. */
  function refresh() {
    grid = doc.querySelector('[data-sbl-grid]') || doc.getElementById('product-grid');
    ensureHead();
    watch();
    sync();
  }
  window.SRStoneList = { refresh: refresh };

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
